import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import type { ScopeLevel, TargetHistoryRow } from '../api/types';
import { useApp } from '../state/AppContext';
import { EmptyRow, ErrorBanner, SortableHeader, formatDateTime, formatLongDate, useTableSort } from '../components/ui';

interface TargetsResponse { today: string; rows: TargetHistoryRow[] }

const LEVEL_LABEL: Record<ScopeLevel, string> = {
  network: 'Every location',
  group: 'A group',
  location: 'One location',
};

/**
 * Minimum on hand per location, per item. Set once for every location, then
 * overridden for a group or a single location where that is different — the
 * most specific target in effect wins. Every target has a start date, today or
 * later, so raising one never changes whether a past week was short.
 */
export function Targets() {
  const { reference, canEditSetup } = useApp();
  const queryClient = useQueryClient();
  const [equipmentFilter, setEquipmentFilter] = useState('all');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [draft, setDraft] = useState({
    equipmentTypeId: '', scopeLevel: 'network' as ScopeLevel, scopeId: '', minimum: '', effectiveFrom: reference.today,
  });

  const targets = useQuery({ queryKey: ['targets'], queryFn: () => api.get<TargetsResponse>('/api/setup/targets') });
  const today = targets.data?.today ?? reference.today;
  const rows = useMemo(() => targets.data?.rows ?? [], [targets.data]);

  // What is in effect today at each level: the newest started row for each scope.
  const current = useMemo(() => {
    const map = new Map<string, TargetHistoryRow>();
    for (const r of rows) {
      if (r.effectiveFrom > today) continue;
      const key = `${r.equipmentTypeId}|${r.scopeLevel}|${r.scopeId ?? ''}`;
      if (!map.has(key)) map.set(key, r);
    }
    return [...map.values()];
  }, [rows, today]);

  const summary = reference.equipment.filter((e) => e.isActive).map((e) => {
    const mine = current.filter((r) => r.equipmentTypeId === e.equipmentTypeId && r.minimum != null);
    return {
      equipment: e,
      network: mine.find((r) => r.scopeLevel === 'network')?.minimum ?? null,
      groups: mine.filter((r) => r.scopeLevel === 'group').length,
      locations: mine.filter((r) => r.scopeLevel === 'location').length,
      scheduled: rows.filter((r) => r.equipmentTypeId === e.equipmentTypeId && r.effectiveFrom > today).length,
    };
  });

  const add = useMutation({
    mutationFn: () =>
      api.post('/api/setup/targets', {
        equipmentTypeId: Number(draft.equipmentTypeId),
        scopeLevel: draft.scopeLevel,
        scopeId: draft.scopeLevel === 'network' ? null : Number(draft.scopeId),
        minimum: draft.minimum.trim() === '' ? null : Number(draft.minimum),
        effectiveFrom: draft.effectiveFrom,
      }),
    onSuccess: () => {
      setError(null);
      setNotice('Target saved.');
      setDraft({ ...draft, minimum: '' });
      void queryClient.invalidateQueries({ queryKey: ['targets'] });
      void queryClient.invalidateQueries({ queryKey: ['rollup'] });
      void queryClient.invalidateQueries({ queryKey: ['count-sheet'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const ready = draft.equipmentTypeId !== '' && (draft.scopeLevel === 'network' || draft.scopeId !== '') && draft.effectiveFrom >= today;
  const shown = rows.filter((r) => equipmentFilter === 'all' || String(r.equipmentTypeId) === equipmentFilter);
  const summarySort = useTableSort(summary, {
    supply: (s) => s.equipment.name,
    network: (s) => s.network,
    groups: (s) => s.groups,
    locations: (s) => s.locations,
    scheduled: (s) => s.scheduled,
  });
  const historySort = useTableSort(shown, {
    starts: (r) => r.effectiveFrom,
    supply: (r) => r.equipment,
    scope: (r) => r.scopeName ?? LEVEL_LABEL[r.scopeLevel],
    minimum: (r) => r.minimum,
    by: (r) => r.createdBy,
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Targets</div>
          <div className="page-subtitle">Minimum on hand per location — for every location, a group, or one location</div>
        </div>
      </div>

      {!canEditSetup && (
        <div className="meta" style={{ marginBottom: 12 }}>You can see the targets. Setting them needs Setup at all regions.</div>
      )}
      {notice && <div className="banner banner--info"><div>{notice}</div></div>}
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      {targets.isError && <ErrorBanner message={(targets.error as Error).message} />}

      {canEditSetup && (
        <div className="card" style={{ marginBottom: 18 }}>
          <div className="section-label">SET A TARGET</div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <label className="field-label" htmlFor="t-equipment">SUPPLY</label>
              <select id="t-equipment" className="control" value={draft.equipmentTypeId}
                onChange={(e) => setDraft({ ...draft, equipmentTypeId: e.target.value })}>
                <option value="">Choose…</option>
                {reference.equipment.filter((e) => e.isActive).map((e) => (
                  <option key={e.equipmentTypeId} value={e.equipmentTypeId}>{e.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="field-label" htmlFor="t-level">FOR</label>
              <select id="t-level" className="control" value={draft.scopeLevel}
                onChange={(e) => setDraft({ ...draft, scopeLevel: e.target.value as ScopeLevel, scopeId: '' })}>
                {(Object.keys(LEVEL_LABEL) as ScopeLevel[]).map((l) => <option key={l} value={l}>{LEVEL_LABEL[l]}</option>)}
              </select>
            </div>
            {draft.scopeLevel === 'group' && (
              <div>
                <label className="field-label" htmlFor="t-group">GROUP</label>
                <select id="t-group" className="control" value={draft.scopeId} onChange={(e) => setDraft({ ...draft, scopeId: e.target.value })}>
                  <option value="">Choose…</option>
                  {reference.groups.map((g) => <option key={g.groupId} value={g.groupId}>{g.name}</option>)}
                </select>
              </div>
            )}
            {draft.scopeLevel === 'location' && (
              <div>
                <label className="field-label" htmlFor="t-location">LOCATION</label>
                <select id="t-location" className="control" value={draft.scopeId} onChange={(e) => setDraft({ ...draft, scopeId: e.target.value })}>
                  <option value="">Choose…</option>
                  {reference.locations.filter((l) => l.isActive).map((l) => (
                    <option key={l.locationId} value={l.locationId}>{l.code}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="field-label" htmlFor="t-minimum">MINIMUM ON HAND</label>
              <input id="t-minimum" className="control" style={{ width: 110 }} inputMode="numeric" placeholder="none"
                value={draft.minimum} onChange={(e) => setDraft({ ...draft, minimum: e.target.value.replace(/[^\d]/g, '') })} />
            </div>
            <div>
              <label className="field-label" htmlFor="t-from">STARTS</label>
              <input id="t-from" type="date" className="control" min={today} value={draft.effectiveFrom}
                onChange={(e) => setDraft({ ...draft, effectiveFrom: e.target.value })} />
            </div>
            <button className="btn btn--primary" disabled={!ready || add.isPending} onClick={() => { setNotice(null); add.mutate(); }}>
              {add.isPending ? 'Saving…' : 'Save target'}
            </button>
          </div>
          <div className="meta" style={{ marginTop: 8 }}>
            A minimum is per location: 50 for every location means each location that carries the item should have 50
            on hand. Leave the minimum blank to remove the target at that level from the start date, so the next level up
            applies. Nothing is overwritten — every target keeps its history below.
          </div>
        </div>
      )}

      <div className="section-label">IN EFFECT TODAY</div>
      <div className="table-wrap" style={{ marginBottom: 22 }}>
        <table className="table">
          <thead>
            <tr>
              <SortableHeader label="SUPPLY" column="supply" sort={summarySort.sort} onSort={summarySort.onSort} />
              <SortableHeader label="EVERY LOCATION" column="network" sort={summarySort.sort} onSort={summarySort.onSort} style={{ textAlign: 'center' }} />
              <SortableHeader label="GROUP OVERRIDES" column="groups" sort={summarySort.sort} onSort={summarySort.onSort} style={{ textAlign: 'center' }} />
              <SortableHeader label="LOCATION OVERRIDES" column="locations" sort={summarySort.sort} onSort={summarySort.onSort} style={{ textAlign: 'center' }} />
              <SortableHeader label="SCHEDULED" column="scheduled" sort={summarySort.sort} onSort={summarySort.onSort} style={{ textAlign: 'center' }} />
            </tr>
          </thead>
          <tbody>
            {summarySort.rows.map((s) => (
              <tr key={s.equipment.equipmentTypeId}>
                <td className="strong">{s.equipment.name}</td>
                <td style={{ textAlign: 'center' }}>{s.network != null ? s.network.toLocaleString() : <span className="meta">Set target</span>}</td>
                <td style={{ textAlign: 'center' }}>{s.groups || '—'}</td>
                <td style={{ textAlign: 'center' }}>{s.locations || '—'}</td>
                <td style={{ textAlign: 'center' }}>{s.scheduled || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="filter-row">
        <div className="section-label" style={{ marginBottom: 0 }}>EVERY TARGET</div>
        <select className="control" aria-label="Supply" value={equipmentFilter} onChange={(e) => setEquipmentFilter(e.target.value)}>
          <option value="all">All supplies</option>
          {reference.equipment.map((e) => <option key={e.equipmentTypeId} value={e.equipmentTypeId}>{e.name}</option>)}
        </select>
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <SortableHeader label="STARTS" column="starts" sort={historySort.sort} onSort={historySort.onSort} />
              <SortableHeader label="SUPPLY" column="supply" sort={historySort.sort} onSort={historySort.onSort} />
              <SortableHeader label="FOR" column="scope" sort={historySort.sort} onSort={historySort.onSort} />
              <SortableHeader label="MINIMUM" column="minimum" sort={historySort.sort} onSort={historySort.onSort} className="num" />
              <SortableHeader label="SET BY" column="by" sort={historySort.sort} onSort={historySort.onSort} />
            </tr>
          </thead>
          <tbody>
            {targets.isLoading && <EmptyRow colSpan={5}>Loading…</EmptyRow>}
            {!targets.isLoading && shown.length === 0 && (
              <EmptyRow colSpan={5}>No targets yet. Until one is set, the rollup shows SET TARGET rather than a silent zero.</EmptyRow>
            )}
            {historySort.rows.map((r) => (
              <tr key={r.targetId}>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {formatLongDate(r.effectiveFrom)}
                  {r.effectiveFrom > today && <div className="meta">scheduled</div>}
                </td>
                <td className="strong">{r.equipment}</td>
                <td>{r.scopeName ?? LEVEL_LABEL[r.scopeLevel]}</td>
                <td className="num">{r.minimum != null ? r.minimum.toLocaleString() : <span className="meta">removed</span>}</td>
                <td className="meta">{r.createdBy} · {formatDateTime(r.createdAtUtc)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
